import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  timer,
  takeWhile,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { TenantService } from './tenant.service';

export type ReceiptOrderItem = {
  itemName: string;
  itemTotalGrossOriginal: string;
  itemQuantity: number;
  itemNote?: string | null;
  extras: Array<{
    extraName?: string;
    extraPrice?: string | number;
    extraQuantity?: number;
  }>;
};

export type ReceiptOrder = {
  id: number;
  publicOrderCode: string;
  orderStatus: string;
  orderType: 'delivery' | 'collection';
  orderTotalCurrentGross: string;
  paymentStatus: string;
  comment?: string | null;
  tenantComment?: string | null;
  matchedEmail: string;
  createdAt: string;
  createdAtLocal?: string;
  orderFeeOriginalGross?: string | number;
  deliveryFeeGross?: string | number;
  smallOrderFeeGross?: string | number;
  delivery?: {
    address_line_1?: string;
    address_line_2?: string | null;
    city?: string;
    county?: string;
    country?: string;
    postal_code?: string;
  } | null;
  details: ReceiptOrderItem[];
};

export type ViewReceiptResult = {
  status: 'success' | 'error';
  order: ReceiptOrder | null;
  message?: string;
};

export type UiOrderStatus =
  | 'PLACED'
  | 'CONFIRMED'
  | 'PREPARING'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'CANCELLED';

export interface OrderStatusResult {
  uiStatus: UiOrderStatus;
  backendStatusRaw: string;
  updatedAt?: string; // UTC ISO if present
  updatedAtLocal?: string; // "YYYY-MM-DD HH:mm:ss" (no TZ)
  createdAt?: string; // UTC ISO if present
  createdAtLocal?: string; // "YYYY-MM-DD HH:mm:ss" (no TZ)
  paymentStatus?: string;
  refund?: { reason?: string; totalOriginal?: string; totalModified?: string };
  publicOrderCode: string;
  cancel?: {
    paymentStatus?: string;
    reason?: string;
    totalOriginal?: string;
    totalModified?: string;
  };
  orderType: string;
}

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private checkoutSubject = new Subject<void>();
  checkout$ = this.checkoutSubject.asObservable();

  constructor(
    private router: Router,
    private http: HttpClient,
    private tenantService: TenantService,
  ) {}

  triggerCheckout() {
    this.checkoutSubject.next(); // Notify listeners (Waitron)
  }

  navigateToCheckout() {
    this.router.navigate(['/checkout']); // Navigate to checkout page
  }

  getOrderStatus(
    orderId: number,
    guestId?: number,
  ): Observable<OrderStatusResult> {
    let params = new HttpParams()
      .set('action', 'checkOrderStatus')
      .set('orderId', String(orderId));
    if (guestId != null) params = params.set('guestId', String(guestId));

    const url = environment.orderUrl;
    return this.http.get<any>(url, { params }).pipe(
      map((res) => {
        const raw = this.extractBackendStatus(res);

        const paymentStatus = String(
          res?.data?.paymentStatus ??
            res?.data?.payment_status ??
            res?.paymentStatus ??
            res?.payment_status ??
            '',
        ).toLowerCase();

        const cancel =
          raw === 'cancelled'
            ? {
                paymentStatus: res?.data?.paymentStatus ?? res?.paymentStatus,
                reason:
                  res?.data?.orderTotalCurrentReason ??
                  res?.data?.cancel_reason ??
                  res?.cancel_reason,
                totalOriginal: res?.data?.productTotalOriginalGross,
                totalModified: res?.data?.orderTotalCurrentGross,
              }
            : undefined;

        const refund =
          paymentStatus === 'refunded'
            ? {
                reason: res?.data?.orderTotalCurrentReason,
                totalOriginal: res?.data?.productTotalOriginalGross,
                totalModified: res?.data?.orderTotalCurrentGross,
              }
            : undefined;

        return {
          uiStatus: this.mapBackendStatusToUi(raw),
          backendStatusRaw: raw,

          // Pass through timestamps from any likely place
          updatedAt:
            res?.data?.updatedAt ?? res?.updatedAt ?? res?.order?.updatedAt,
          createdAt:
            res?.data?.createdAt ?? res?.createdAt ?? res?.order?.createdAt,

          updatedAtLocal: res?.data?.updatedAtLocal ?? res?.updatedAtLocal,
          createdAtLocal: res?.data?.createdAtLocal ?? res?.createdAtLocal,

          paymentStatus,
          refund,
          cancel,
          orderType: res.data.orderType,
          publicOrderCode: res.data.publicOrderCode,
        } as OrderStatusResult;
      }),
    );
  }

  /** Poll every `pollMs` until status changes; consumer decides when to stop */
  watchOrderStatus(
    orderId: number,
    guestId?: number,
    pollMs = 15000,
  ): Observable<OrderStatusResult> {
    return timer(0, pollMs).pipe(
      switchMap(() => this.getOrderStatus(orderId, guestId)),
      distinctUntilChanged(
        (a, b) =>
          a.backendStatusRaw === b.backendStatusRaw &&
          (a.paymentStatus ?? '') === (b.paymentStatus ?? ''),
      ),
      takeWhile(
        (res) =>
          res.uiStatus !== 'DELIVERED' &&
          res.uiStatus !== 'CANCELLED' &&
          (res.paymentStatus ?? '') !== 'refunded',
        true,
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  viewReceipt(
    orderId: number,
    clientEmail: string,
  ): Observable<ViewReceiptResult> {
    let params = new HttpParams()
      .set('action', 'viewOrder')
      .set('orderId', String(orderId))
      .set('email', clientEmail);

    const url = environment.orderUrl;
    return this.http.get<any>(url, { params }).pipe(
      map((res): ViewReceiptResult => {
        if (
          res?.status !== 'success' ||
          !Array.isArray(res?.orderDetails) ||
          !res.orderDetails[0]
        ) {
          return {
            status: 'error',
            order: null,
            message: res?.message ?? 'Receipt not found',
          };
        }
        const order: ReceiptOrder = res.orderDetails[0];
        return { status: 'success', order };
      }),
    );
  }

  // --- Helpers ---

  /** Try common locations/keys for a status field (rename if your API is known). */
  private extractBackendStatus(res: any): string {
    const guess = res?.data?.orderStatus;
    return String(guess ?? '').toLowerCase();
  }

  private mapBackendStatusToUi(raw: string): UiOrderStatus {
    const s = raw.toLowerCase();
    if (['cancelled', 'canceled', 'rejected', 'declined'].includes(s))
      return 'CANCELLED';
    if (['confirmed', 'accepted', 'approved'].includes(s)) return 'CONFIRMED';
    if (['preparing', 'cooking', 'in_kitchen'].includes(s)) return 'PREPARING';
    if (['delivery'].includes(s)) return 'OUT_FOR_DELIVERY';
    if (['delivered', 'complete', 'completed'].includes(s)) return 'DELIVERED';
    if (['placed', 'created', 'received', 'pending'].includes(s))
      return 'PLACED';
    return 'PLACED';
  }
}
